// CHAIN-VIEW (A16): the Live canvas's chain block. A task's request drawn as stations,
// requester → DE → helpers → delivery, each with its status (waiting / working / done / failed)
// and its output on click; a station that made nothing reads "no output, handed to X with this
// ask". The data is the chain read (`liveCanvas.chain`: helper registry, cards, handoffs), served
// only for a task the viewer may read; a refusal renders nothing. The query sits under the Work
// key root, so the live channel's `task.changed` frames refetch it, and `helper.commitment` frames
// (unmapped kind: whole company) do too.
import type { ChainNode, ChainView } from "@companyos/schema/contracts";
import { ApiError } from "@companyos/sdk";
import { cn } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import {
  CheckIcon,
  CircleAlertIcon,
  CircleDashedIcon,
  LoaderIcon,
  WorkflowIcon,
} from "lucide-react";
import { Fragment, useState } from "react";
import type { Api } from "../../api/client";

export const chainKey = (co: string, taskId: string) =>
  ["company", co, "work", "chain", taskId] as const;

const STATUS_WORD: Record<ChainNode["status"], string> = {
  waiting: "Waiting",
  working: "Working",
  done: "Done",
  failed: "Failed",
};

/** What a station says when it has nothing to open. */
export function noOutputLine(n: ChainNode): string | null {
  if (n.output || !n.handed_to) return null;
  return `No output, handed to ${n.handed_to}${n.ask ? ` with this ask: ${n.ask}` : ""}`;
}

export function ChainDiagram({ view }: { view: ChainView }) {
  const [open, setOpen] = useState<string | null>(null);
  if (!view.chain)
    return <p className="text-sm text-muted-foreground">No request on this task yet.</p>;
  const nodes = view.chain.nodes;
  const shown = nodes.find((n) => n.id === open) ?? null;
  return (
    <div className="flex flex-col gap-3" data-slot="chain-diagram">
      <p className="truncate text-xs text-muted-foreground">{view.chain.title}</p>
      <ol className="flex flex-wrap items-stretch gap-2">
        {nodes.map((n, i) => (
          <Fragment key={n.id}>
            {i > 0 ? (
              <li aria-hidden="true" className="self-center text-muted-foreground">
                {"→"}
              </li>
            ) : null}
            <li>
              <button
                type="button"
                data-node={n.id}
                data-role={n.role}
                data-status={n.status}
                aria-pressed={open === n.id}
                onClick={() => setOpen(open === n.id ? null : n.id)}
                className={cn(
                  "flex min-w-24 flex-col items-start gap-0.5 rounded-md border border-border px-2 py-1.5 text-left text-xs",
                  n.status === "working" && "border-primary",
                  n.status === "failed" && "border-destructive",
                  open === n.id && "bg-primary-soft",
                )}
              >
                <span className="flex items-center gap-1 font-medium">
                  <StatusIcon status={n.status} />
                  {n.label}
                </span>
                <span className="text-muted-foreground">{STATUS_WORD[n.status]}</span>
              </button>
            </li>
          </Fragment>
        ))}
      </ol>
      {shown ? (
        <div data-slot="chain-output" className="rounded-md bg-sunken p-2 text-sm">
          <p className="mb-1 text-xs font-medium">
            {shown.label}
            {shown.detail ? ` · ${shown.detail}` : ""}
          </p>
          {shown.output ? (
            <p className="whitespace-pre-wrap break-words">{shown.output}</p>
          ) : (
            <p className="text-muted-foreground">
              {noOutputLine(shown) ?? (shown.status === "done" ? "No output." : "Nothing yet.")}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

function StatusIcon({ status }: { status: ChainNode["status"] }) {
  const cls = "size-3 shrink-0";
  if (status === "done")
    return <CheckIcon aria-hidden="true" className={cn(cls, "text-success")} />;
  if (status === "failed")
    return <CircleAlertIcon aria-hidden="true" className={cn(cls, "text-destructive")} />;
  if (status === "working")
    return <LoaderIcon aria-hidden="true" className={cn(cls, "animate-spin text-primary")} />;
  return <CircleDashedIcon aria-hidden="true" className={cn(cls, "text-muted-foreground")} />;
}

/** The query alone (the canvas wraps it in its card; a refused read leaves the block out). */
export function useChain(api: Api, co: string, taskId: string) {
  return useQuery<ChainView>({
    queryKey: chainKey(co, taskId),
    queryFn: () =>
      api.client.liveCanvas.chain({
        params: { co },
        query: { task_id: taskId },
      }) as Promise<ChainView>,
    meta: { quietStatuses: [403, 404] },
    retry: false,
  });
}

export const chainRefused = (e: unknown) =>
  e instanceof ApiError && (e.status === 403 || e.status === 404);

export { WorkflowIcon as ChainIcon };
